// Crowd-aware heatmap kernel sizing — SHARED by every crime heat layer (owner 2026-08-23).
//
// A heatmap radius expressed only in zoom stops is a FIXED pixel kernel, so the busier the area the
// worse it reads: each point's blur covers the same screen area no matter how many neighbours it has,
// the summed density clamps to the top of the colour ramp everywhere, and the map renders as one
// featureless blob ("blurred blobs crossing each other, 0 usable info").
//
// Measured on the district map: Camden, 578 points all weight 1, 37px kernels in a ~430px pane —
// coverage ≈ 14x oversubscribed, and the whole borough painted a single brown mass. The SPA crime
// layers (crime-live-heat / crime-heat) are milder — readable at z15+ — but merge into one lobe at
// z13, the lowest zoom they run at.
//
// `crowdFactor` shrinks the kernel as points pile up, which is what keeps neighbouring clusters
// separate. It is deliberately a NO-OP below ~70 points: sparse rural districts were tuned UP by
// ALMANAC-4 D4 to fix the opposite complaint ("the map read as 2 faint dots"), and that fix must
// keep working. Clamped at 3 so a very dense cell can't shrink the kernel to invisibility, with an
// 8px floor for the same reason.
//
// Lives in its own module because the same layer paint is defined in FOUR places (district-map.ts
// plus three sites in main.ts) — fixing one and missing the others is exactly how this shipped.
export function crowdFactor(points: number): number {
  return Math.max(1, Math.min(3, Math.sqrt(points / 70)));
}

/**
 * A `heatmap-radius` zoom expression with both stops scaled down for how many points are on screen.
 * Pass the CURRENT feature count — for a source whose data reloads (the viewport-loaded crime-live
 * layer) re-apply this with setPaintProperty after each setData, or the kernel keeps the previous
 * cell's sizing.
 */
export function heatRadius(points: number, zLo: number, rLo: number, zHi: number, rHi: number): unknown[] {
  const c = crowdFactor(points);
  const r = (x: number): number => Math.max(8, Math.round(x / c));
  return ['interpolate', ['linear'], ['zoom'], zLo, r(rLo), zHi, r(rHi)];
}

/**
 * A `heatmap-weight` expression reading the per-location crime count.
 *
 * COMPRESSED, not linear, and deliberately so. Some forces record a whole borough's crime against
 * their police station's coordinate, so one marker can carry tens of crimes that did not happen there
 * (owner 2026-08-23). Under a linear weight that single point would dominate the entire map. A
 * log-shaped ramp lets a genuinely busy location read hotter than a quiet one without letting a
 * recording artefact drown the real pattern — the map still says "busier here", just not 50x busier.
 * The surfaces carry a note explaining the artefact; this stops it distorting the picture as well.
 *
 * `sparse` keeps ALMANAC-4 D4's floor for thin rural districts, where a single crime must still show.
 */
export function heatWeight(sparse = false): unknown[] {
  const base = sparse ? 0.6 : 0.35;
  return ['interpolate', ['linear'], ['coalesce', ['to-number', ['get', 'weight']], 1],
    1, base, 4, base + (1 - base) * 0.45, 16, base + (1 - base) * 0.78, 64, 1];
}
